import { Gender, SupportedMatchInfoHashCategory, UserModel, isBeautyMatchingCategory, userDataCompleted } from '@vestico/model';

import { i18n } from '@vestico/i18n';

import BodyInfoForm from '../routes/BodyInfoForm';
import BraForm from '../routes/BraForm';
import { ShapeInputHip } from '../routes/ShapeInputHip';
import { ShapeInputShoulder } from '../routes/ShapeInputShoulder';
import { ShapeInputTummy } from '../routes/ShapeInputTummy';
import { AgeForm } from '../routes/beauty/AgeForm';
import { HairToneForm } from '../routes/beauty/HairToneForm';
import SkinSensitivityForm from '../routes/beauty/SkinSensitivityForm';
import { SkinToneForm } from '../routes/beauty/SkinToneForm';
import { SkinTypeForm } from '../routes/beauty/SkinTypeForm';
import { UndertoneInput } from '../routes/beauty/UndertoneInput';
import { StepConfig } from './step';

export const RoutePathEnterBasicInfo = '/basicInfo';
export const RoutePathSelectBraSize = '/brasize';
export const RoutePathSelectShoulder = '/shoulder';
export const RoutePathSelectTummy = '/tummy';
export const RoutePathSelectHip = '/hip';
export const RoutePathMatchedResults = '/matchedResults';
export const RoutePathEnterSkinTone = '/skinTone';
export const RoutePathEnterSkinUndertone = '/skinUndertone';
export const RoutePathEnterSkinType = '/skinType';
export const RoutePathEnterHairTone = '/hairTone';
export const RoutePathEnterEyeColor = '/eyeColor';
export const RoutePathEnterSkinSensitivity = '/skinSensitivity';
export const RoutePathEnterAge = '/age';

const getBodyInfoStepsFashion = (user?: UserModel) => {
  const bodyInfoSteps: StepConfig[] = [
    {
      path: RoutePathEnterBasicInfo,
      title: i18n.t('LOC_ENTER_INFO_STEP_BASIC_INFO', { ns: 'shared' }),
      class: BodyInfoForm,
    },
  ];

  if (user?.gender === Gender.male) {
    bodyInfoSteps.push(
      {
        path: RoutePathSelectShoulder,
        title: i18n.t('LOC_ENTER_INFO_STEP_SHOULDER_MALE', { ns: 'shared' }),
        class: ShapeInputShoulder,
      },
      {
        path: RoutePathSelectTummy,
        title: i18n.t('LOC_ENTER_INFO_STEP_BELLY_MALE', { ns: 'shared' }),
        class: ShapeInputTummy,
      }
    );
  } else {
    bodyInfoSteps.push(
      {
        path: RoutePathSelectBraSize,
        title: i18n.t('LOC_ENTER_INFO_STEP_BRA_SIZE', { ns: 'shared' }),
        class: BraForm,
      },
      {
        path: RoutePathSelectTummy,
        title: i18n.t('LOC_ENTER_INFO_STEP_TUMMY', { ns: 'shared' }),
        class: ShapeInputTummy,
      },
      {
        path: RoutePathSelectHip,
        title: i18n.t('LOC_ENTER_INFO_STEP_HIP', { ns: 'shared' }),
        class: ShapeInputHip,
      }
    );
  }

  return bodyInfoSteps;
};

const getBodyInfoStepsBeauty = (user?: UserModel, matchingCategory?: SupportedMatchInfoHashCategory, hideAgeQuestion?: boolean): StepConfig[] => {
  const SKIN_TONE_STEP: StepConfig = {
    path: RoutePathEnterSkinTone,
    title: i18n.t('LOC_ENTER_INFO_STEP_SKIN_TONE', { ns: 'shared' }),
    class: SkinToneForm,
  };

  const SKIN_UNDERTONE_STEP: StepConfig = {
    path: RoutePathEnterSkinUndertone,
    title: i18n.t('LOC_ENTER_INFO_STEP_SKIN_UNDERTONE', { ns: 'shared' }),
    class: UndertoneInput,
  };

  const SKIN_TYPE_STEP: StepConfig = {
    path: RoutePathEnterSkinType,
    title: i18n.t('LOC_ENTER_INFO_STEP_SKIN_TYPE', { ns: 'shared' }),
    class: SkinTypeForm,
  };

  const HAIR_TONE_STEP: StepConfig = {
    path: RoutePathEnterHairTone,
    title: i18n.t('LOC_ENTER_INFO_STEP_HAIR_TONE', { ns: 'shared' }),
    class: HairToneForm,
  };

  const AGE_QUESTION: StepConfig = {
    path: RoutePathEnterAge,
    title: i18n.t('LOC_ENTER_INFO_STEP_AGE', { ns: 'shared' }),
    class: AgeForm,
  };

  const SKIN_SENSITIVITY_QUESTION: StepConfig = {
    path: RoutePathEnterSkinSensitivity,
    title: i18n.t('LOC_ENTER_INFO_STEP_SKIN_SENSITIVTY', { ns: 'shared' }),
    class: SkinSensitivityForm,
  };

  const beautyInfoSteps: StepConfig[] =
    matchingCategory === 'skincare'
      ? [SKIN_TYPE_STEP, SKIN_SENSITIVITY_QUESTION]
      : [SKIN_TONE_STEP, SKIN_UNDERTONE_STEP, SKIN_TYPE_STEP, HAIR_TONE_STEP];

  if (!user?.birthyear && !hideAgeQuestion) {
    beautyInfoSteps.push(AGE_QUESTION);
  }

  return beautyInfoSteps;
};
const getBodyInfoSteps = (user?: UserModel, matchingCategory?: SupportedMatchInfoHashCategory, hideAgeQuestion?: boolean): StepConfig[] => {
  if (isBeautyMatchingCategory(matchingCategory)) {
    return getBodyInfoStepsBeauty(user, matchingCategory, hideAgeQuestion);
  }

  // For size rec to show body info even when matching category is 'default'
  return getBodyInfoStepsFashion(user);
};

export const getStepConfigs = (
  user: UserModel | undefined | null,
  matchingCategory?: SupportedMatchInfoHashCategory,
  additionalPreSteps?: StepConfig[],
  additionalPostSteps?: StepConfig[],
  showStepsIfCompleted?: boolean,
  hideAgeQuestion?: boolean
): StepConfig[] => {
  const steps: StepConfig[] = [];
  if (additionalPreSteps) {
    steps.push(...additionalPreSteps);
  }

  if (user && (showStepsIfCompleted || !userDataCompleted(user, matchingCategory, false, true))) {
    steps.push(...getBodyInfoSteps(user, matchingCategory, hideAgeQuestion));
  }

  if (additionalPostSteps) {
    steps.push(...additionalPostSteps);
  }

  return steps;
};
